/**
 * 智能布局管理器
 * 实现空间优先的碎片布局算法
 */
import { Vector2, ComplexityLevel, Quadrant, LayoutConfig, PieceLayoutData } from '../types';
import { PuzzlePiece } from './PuzzlePiece';
import { SlicedPiece } from './ImageSlicer';
import { EdgeGenerator } from '../core/EdgeGenerator';
import { ComplexityAnalyzer, ComplexityResult } from './ComplexityAnalyzer';
import { Random } from '../core/Random';

// 布局区域边界
interface LayoutBounds {
    minX: number;
    maxX: number;
    minY: number;
    maxY: number;
}

// 象限边界（带模糊偏移）
interface QuadrantBounds {
    [Quadrant.TOP_LEFT]: LayoutBounds;
    [Quadrant.TOP_RIGHT]: LayoutBounds;
    [Quadrant.BOTTOM_LEFT]: LayoutBounds;
    [Quadrant.BOTTOM_RIGHT]: LayoutBounds;
}

export class SmartLayoutManager {
    private config: LayoutConfig;
    private complexityAnalyzer: ComplexityAnalyzer;
    private random: Random;

    // 布局区域
    private areaWidth: number = 1024;
    private areaHeight: number = 1024;
    private screenBounds: { minX: number, maxX: number, minY: number, maxY: number } | null = null;
    private centralBounds: LayoutBounds | null = null;
    private edgeRingBounds: LayoutBounds | null = null;
    private quadrantBounds: QuadrantBounds | null = null;

    // 布局数据缓存
    private layoutData: Map<string, PieceLayoutData> = new Map();

    // 【调试开关】是否启用随机旋转，设为 false 可禁用随机旋转便于调试
    private enableRandomRotation: boolean = true;

    constructor(config?: Partial<LayoutConfig>, seed: number = Date.now()) {
        this.config = {
            centralAreaRatio: 0.5,      // 中央工作区占 50%
            edgeRingWidth: 80,          // Edge Ring 宽度
            quadrantFuzziness: 30,      // 象限边界模糊度
            minPieceDistance: 20,       // 碎片间最小距离
            enableVisualDim: true,      // 启用视觉退让
            ...config
        };

        this.complexityAnalyzer = new ComplexityAnalyzer();
        this.random = new Random(seed);
    }

    /**
     * 初始化布局区域
     */
    initializeLayout(width: number, height: number): void {
        this.areaWidth = width;
        this.areaHeight = height;
        
        // 默认屏幕边界等于逻辑区域
        if (!this.screenBounds) {
            this.screenBounds = { minX: 0, maxX: width, minY: 0, maxY: height };
        }
        
        // 不再此处预计算，等待 performLayout 时根据底板尺寸动态计算
    }

    /**
     * 更新屏幕逻辑边界（允许布局超出 areaWidth/Height 范围）
     * 用于让碎片利用整个屏幕空间，而不仅是底板区域
     */
    updateScreenBounds(minX: number, maxX: number, minY: number, maxY: number): void {
        this.screenBounds = { minX, maxX, minY, maxY };
    }

    /**
     * 极简双翼布局计算
     * 性能 O(1)，直接根据屏幕比例划分
     */
    private calculateLayoutBoundsSideBalanced(): void {
        const bounds = this.screenBounds || { minX: 0, maxX: 1024, minY: 0, maxY: 1024 };
        const screenWidth = bounds.maxX - bounds.minX;
        const centerX = (bounds.maxX + bounds.minX) / 2;

        // 核心逻辑：禁区宽度为屏幕的 55%，但最大不超过 1100 逻辑单位
        // 这保证了在超宽屏下侧边栏不会太窄，在窄屏下也能留出空位
        const forbiddenWidth = Math.min(screenWidth * 0.55, 1100);
        
        const leftZone = {
            minX: bounds.minX + 30,
            maxX: centerX - forbiddenWidth / 2,
            minY: bounds.minY + 80, // 增加内缩
            maxY: bounds.maxY - 80  // 增加内缩
        };

        const rightZone = {
            minX: centerX + forbiddenWidth / 2,
            maxX: bounds.maxX - 30,
            minY: bounds.minY + 80, // 增加内缩
            maxY: bounds.maxY - 80  // 增加内缩
        };

        this.centralBounds = {
            minX: centerX - forbiddenWidth / 2,
            maxX: centerX + forbiddenWidth / 2,
            minY: bounds.minY,
            maxY: bounds.maxY
        };

        this.quadrantBounds = {
            [Quadrant.TOP_LEFT]: leftZone,
            [Quadrant.BOTTOM_LEFT]: leftZone,
            [Quadrant.TOP_RIGHT]: rightZone,
            [Quadrant.BOTTOM_RIGHT]: rightZone
        };

        this.edgeRingBounds = {
            minX: bounds.minX + 10,
            maxX: bounds.maxX - 10,
            minY: bounds.minY + 10,
            maxY: bounds.maxY - 10
        };
    }

    /**
     * 计算布局区域边界（保留占位，实际由 SideBalanced 处理）
     */
    private calculateLayoutBounds(): void {
        this.calculateLayoutBoundsSideBalanced();
    }

    /**
     * 执行智能布局
     * @param pieces 所有碎片
     * @param slicedPieces 切割后的碎片（用于复杂度分析）
     * @param edgeGenerator 边缘生成器（用于判断边缘碎片）
     */
    performLayout(
        pieces: PuzzlePiece[],
        slicedPieces: SlicedPiece[],
        edgeGenerator: EdgeGenerator
    ): void {
        this.layoutData.clear();

        // 重新初始化布局区域 (不再扫描碎片，直接根据屏幕比例计算)
        this.calculateLayoutBoundsSideBalanced();

        // Step 2: 分析复杂度
        const complexityResults = this.complexityAnalyzer.analyzeAll(slicedPieces);
        const complexityMap = new Map<string, ComplexityResult>();
        for (const result of complexityResults) {
            complexityMap.set(result.pieceId, result);
        }

        // Step 2: 分类碎片
        const edgePieces: PuzzlePiece[] = [];
        const highComplexityPieces: PuzzlePiece[] = [];
        const mediumComplexityPieces: PuzzlePiece[] = [];
        const lowComplexityPieces: PuzzlePiece[] = [];

        for (const piece of pieces) {
            if (piece.isPlaced) continue;  // 跳过已放置的碎片

            const isEdge = edgeGenerator.isEdgePiece(piece.coord);
            piece.setIsEdge(isEdge);

            const complexity = complexityMap.get(piece.id);
            if (complexity) {
                piece.setComplexityLevel(complexity.level);
            }

            if (isEdge) {
                edgePieces.push(piece);
            } else {
                switch (piece.complexityLevel) {
                    case ComplexityLevel.HIGH:
                        highComplexityPieces.push(piece);
                        break;
                    case ComplexityLevel.MEDIUM:
                        mediumComplexityPieces.push(piece);
                        break;
                    case ComplexityLevel.LOW:
                        lowComplexityPieces.push(piece);
                        break;
                }
            }
        }

        // Step 3: 根据碎片数量选择布局策略
        const totalCount = pieces.length;
        const strategy = this.selectLayoutStrategy(totalCount);

        // 计算最大碎片半径（用于安全边距）
        let maxPieceRadius = 50; // 默认值
        if (slicedPieces.length > 0) {
            for (const sp of slicedPieces) {
                maxPieceRadius = Math.max(maxPieceRadius, sp.bounds.width / 2, sp.bounds.height / 2);
            }
        }
        // 增加一点额外缓冲
        const safeMargin = maxPieceRadius + 10;

        // Step 4: 执行布局
        switch (strategy) {
            case 'centered':
                // 中心化安全布局（适用于碎片极少且巨大的情况，如 rings=1）
                this.layoutCornerScatter(pieces, safeMargin);
                break;

            case 'simple':
                // 全铺模式（≤40 碎片）
                this.layoutSimple([...edgePieces, ...highComplexityPieces,
                                   ...mediumComplexityPieces, ...lowComplexityPieces]);
                break;

            case 'edgeRing':
                // Edge Ring + 中央模式（40-80 碎片）
                this.layoutEdgeRing(edgePieces);
                this.layoutQuadrants([...highComplexityPieces, ...mediumComplexityPieces,
                                      ...lowComplexityPieces]);
                break;

            case 'dualRing':
                // Edge Ring + 纹理双环（80-150 碎片）
                this.layoutEdgeRing(edgePieces);
                this.layoutByComplexityRings(highComplexityPieces, mediumComplexityPieces,
                                             lowComplexityPieces, 2);
                break;

            case 'tripleRing':
                // 三环 + 视觉退让（150+ 碎片）
                this.layoutEdgeRing(edgePieces);
                this.layoutByComplexityRings(highComplexityPieces, mediumComplexityPieces,
                                             lowComplexityPieces, 3);
                break;
        }

        console.log(`智能布局完成: ${totalCount} 个碎片, 策略: ${strategy}`);
        console.log(`  边缘碎片: ${edgePieces.length}`);
        console.log(`  高复杂度: ${highComplexityPieces.length}`);
        console.log(`  中复杂度: ${mediumComplexityPieces.length}`);
        console.log(`  低复杂度: ${lowComplexityPieces.length}`);
    }

    /**
     * 选择布局策略
     */
    private selectLayoutStrategy(totalCount: number): 'centered' | 'simple' | 'edgeRing' | 'dualRing' | 'tripleRing' {
        if (totalCount <= 20) return 'centered';
        if (totalCount <= 40) return 'simple';
        if (totalCount <= 80) return 'edgeRing';
        if (totalCount <= 150) return 'dualRing';
        return 'tripleRing';
    }

    /**
     * 四角散布式布局 (Corner Scatter) - 防重叠增强版
     * 使用候选点评分机制，确保碎片均匀散开且不重叠
     */
    private layoutCornerScatter(pieces: PuzzlePiece[], margin: number): void {
        const centerX = this.areaWidth / 2;
        const centerY = this.areaHeight / 2;

        const bounds = this.screenBounds || { minX: 0, maxX: this.areaWidth, minY: 0, maxY: this.areaHeight };
        const pieceRadius = margin - 10;
        const safetyPadding = pieceRadius + 20;
        
        // 中央禁区：严格保护底板
        const keepOutRadius = pieceRadius * 1.6;

        // 左右侧重心点（不再使用四角）
        const sideGravities = [
            { x: bounds.minX + safetyPadding, y: centerY, q: Quadrant.TOP_LEFT }, // 左中
            { x: bounds.maxX - safetyPadding, y: centerY, q: Quadrant.TOP_RIGHT } // 右中
        ];

        console.log(`[Layout] Side Scatter Bounds: X[${bounds.minX.toFixed(0)}, ${bounds.maxX.toFixed(0)}], Y[${bounds.minY.toFixed(0)}, ${bounds.maxY.toFixed(0)}]`);

        const placedPositions: Vector2[] = [];

        for (let i = 0; i < pieces.length; i++) {
            const piece = pieces[i];
            const sideIndex = i % 2;
            const targetCenter = sideGravities[sideIndex];
            
            piece.setHomeQuadrant(targetCenter.q);

            let bestCandidate: Vector2 | null = null;
            let bestScore = -Infinity;

            // 生成 N 个候选点
            const numCandidates = 30;

            for (let k = 0; k < numCandidates; k++) {
                // 在所在侧的垂直带状区域内随机
                const zone = sideIndex === 0 ? this.quadrantBounds![Quadrant.TOP_LEFT] : this.quadrantBounds![Quadrant.TOP_RIGHT];
                
                const candidateX = this.random.uniform(zone.minX, zone.maxX);
                const candidateY = this.random.uniform(zone.minY, zone.maxY);

                // 计算评分
                let minDistToOthers = Number.MAX_VALUE;
                if (placedPositions.length > 0) {
                    for (const pos of placedPositions) {
                        const d = Math.sqrt(Math.pow(candidateX - pos.x, 2) + Math.pow(candidateY - pos.y, 2));
                        minDistToOthers = Math.min(minDistToOthers, d);
                    }
                } else {
                    minDistToOthers = pieceRadius * 5;
                }

                if (minDistToOthers < pieceRadius * 0.8) {
                    minDistToOthers = -10000; 
                }

                // 评分 = 离别人远 + 离所在侧中心近
                const distToSideCenter = Math.abs(candidateY - centerY);
                const score = minDistToOthers * 2 - distToSideCenter * 0.2;

                if (score > bestScore) {
                    bestScore = score;
                    bestCandidate = { x: candidateX, y: candidateY };
                }
            }

            // 如果没找到任何有效点（极端情况），回退到目标角落
            if (!bestCandidate) {
                bestCandidate = { x: targetCenter.x, y: targetCenter.y };
            }

            placedPositions.push(bestCandidate);
            
            const rotation = this.getRotation();
            piece.setPosition(bestCandidate);
            piece.setRotation(rotation);

            this.saveLayoutData(piece, bestCandidate, rotation);
        }
    }

    /**
     * 中心化安全布局（备用回退）
     */
    private layoutCentered(pieces: PuzzlePiece[], margin: number): void {
        const minX = margin;
        const maxX = this.areaWidth - margin;
        const minY = margin;
        const maxY = this.areaHeight - margin;
        // ... (复用之前的逻辑，此处略，因为 layoutSideScatter 会覆盖原 layoutCentered 的位置)
        // 为了代码完整性，这里其实不需要重复实现 layoutCentered，
        // 我们可以让 layoutSideScatter 成为主逻辑，如果失败再调用一个简单的 fallback。
        // 但由于 replace 工具的限制，我必须替换整个函数块。
        // 让我们在下面直接把原 layoutCentered 的逻辑写进去作为 fallback。
        
        const placedPositions: Vector2[] = [];
        const minDistance = margin * 1.5;

        for (const piece of pieces) {
             // ... 简化的全铺逻辑 ...
             const x = this.random.uniform(minX, maxX);
             const y = this.random.uniform(minY, maxY);
             const rotation = this.getRotation();
             piece.setPosition({ x, y });
             piece.setRotation(rotation);
             this.saveLayoutData(piece, { x, y }, rotation);
        }
    }

    /**
     * 简单全铺布局
     */
    private layoutSimple(pieces: PuzzlePiece[]): void {
        // 将所有碎片均匀分配到四象限
        const quadrants = [Quadrant.TOP_LEFT, Quadrant.TOP_RIGHT,
                          Quadrant.BOTTOM_LEFT, Quadrant.BOTTOM_RIGHT];

        for (let i = 0; i < pieces.length; i++) {
            const piece = pieces[i];
            const quadrant = quadrants[i % 4];
            piece.setHomeQuadrant(quadrant);
            this.placePieceInQuadrant(piece, quadrant);
        }
    }

    /**
     * 布局 Edge Ring（边缘碎片强制推向左右极点）
     */
    private layoutEdgeRing(edgePieces: PuzzlePiece[]): void {
        if (!this.edgeRingBounds || !this.screenBounds) return;

        const bounds = this.screenBounds;
        const ringWidth = this.config.edgeRingWidth;

        // 定义极左和极右两个窄带
        const segments = [
            { minX: bounds.minX + 20, maxX: bounds.minX + ringWidth, minY: bounds.minY + 20, maxY: bounds.maxY - 20 },
            { minX: bounds.maxX - ringWidth, maxX: bounds.maxX - 20, minY: bounds.minY + 20, maxY: bounds.maxY - 20 }
        ];

        for (let i = 0; i < edgePieces.length; i++) {
            const piece = edgePieces[i];
            // 0: 左, 1: 右
            const segment = segments[i % 2];

            piece.setHomeQuadrant(Quadrant.EDGE_RING);

            const x = this.random.uniform(segment.minX, segment.maxX);
            const y = this.random.uniform(segment.minY, segment.maxY);
            const rotation = this.getRotation();

            piece.setPosition({ x, y });
            piece.setRotation(rotation);

            this.saveLayoutData(piece, { x, y }, rotation);
        }
    }

    /**
     * 按象限布局碎片
     */
    private layoutQuadrants(pieces: PuzzlePiece[]): void {
        // 打乱顺序
        const shuffled = [...pieces];
        this.shuffleArray(shuffled);

        // 均匀分配到四象限
        const quadrants = [Quadrant.TOP_LEFT, Quadrant.TOP_RIGHT,
                          Quadrant.BOTTOM_LEFT, Quadrant.BOTTOM_RIGHT];

        for (let i = 0; i < shuffled.length; i++) {
            const piece = shuffled[i];
            const quadrant = quadrants[i % 4];
            piece.setHomeQuadrant(quadrant);
            this.placePieceInQuadrant(piece, quadrant);
        }
    }

    /**
     * 按复杂度环形布局
     */
    private layoutByComplexityRings(
        highPieces: PuzzlePiece[],
        mediumPieces: PuzzlePiece[],
        lowPieces: PuzzlePiece[],
        ringCount: number
    ): void {
        if (!this.centralBounds || !this.edgeRingBounds) return;

        const centerX = this.areaWidth / 2;
        const centerY = this.areaHeight / 2;
        const minSize = Math.min(this.areaWidth, this.areaHeight);
        const innerRadius = (minSize * this.config.centralAreaRatio) / 2 + 30;
        const outerRadius = minSize / 2 - this.config.edgeRingWidth - 20;

        if (ringCount === 2) {
            // 双环模式：高复杂度在外，中/低复杂度在内
            this.layoutInRing(highPieces, centerX, centerY, outerRadius - 40, outerRadius);
            this.layoutInRing([...mediumPieces, ...lowPieces], centerX, centerY, innerRadius, outerRadius - 60);
        } else {
            // 三环模式：高 -> 中 -> 低 由外到内
            const ringWidth = (outerRadius - innerRadius) / 3;
            this.layoutInRing(highPieces, centerX, centerY, outerRadius - ringWidth, outerRadius);
            this.layoutInRing(mediumPieces, centerX, centerY, innerRadius + ringWidth, outerRadius - ringWidth - 20);
            this.layoutInRing(lowPieces, centerX, centerY, innerRadius, innerRadius + ringWidth - 20);
        }
    }

    /**
     * 在指定侧边区域内布局碎片（重写以支持左右分栏）
     */
    private layoutInRing(
        pieces: PuzzlePiece[],
        centerX: number,
        centerY: number,
        innerRadius: number,
        outerRadius: number
    ): void {
        // 只保留左右两个象限组
        const leftQuadrants = [Quadrant.TOP_LEFT, Quadrant.BOTTOM_LEFT];
        const rightQuadrants = [Quadrant.TOP_RIGHT, Quadrant.BOTTOM_RIGHT];

        for (let i = 0; i < pieces.length; i++) {
            const piece = pieces[i];
            // 简单交替分配左右
            const isLeft = i % 2 === 0;
            const quadrantPool = isLeft ? leftQuadrants : rightQuadrants;
            const quadrant = quadrantPool[Math.floor(this.random.uniform(0, 2))];
            
            piece.setHomeQuadrant(quadrant);

            // 获取该象限的物理边界
            const bounds = this.quadrantBounds![quadrant as keyof QuadrantBounds];
            
            // 在象限内随机一个点，但尽量靠近中间垂直线（月牙形感）
            const x = this.random.uniform(bounds.minX, bounds.maxX);
            const y = this.random.uniform(bounds.minY, bounds.maxY);
            const rotation = this.getRotation();

            piece.setPosition({ x, y });
            piece.setRotation(rotation);

            this.saveLayoutData(piece, { x, y }, rotation);
        }
    }

    /**
     * 获取象限对应的基准角度
     */
    private getAngleForQuadrant(quadrant: Quadrant): number {
        switch (quadrant) {
            case Quadrant.TOP_LEFT: return -3 * Math.PI / 4;     // 左上 (-135°)
            case Quadrant.TOP_RIGHT: return -Math.PI / 4;        // 右上 (-45°)
            case Quadrant.BOTTOM_LEFT: return 3 * Math.PI / 4;   // 左下 (135°)
            case Quadrant.BOTTOM_RIGHT: return Math.PI / 4;      // 右下 (45°)
            default: return 0;
        }
    }

    /**
     * 在指定象限内放置碎片
     */
    private placePieceInQuadrant(piece: PuzzlePiece, quadrant: Quadrant): void {
        if (!this.quadrantBounds) return;

        const bounds = this.quadrantBounds[quadrant as keyof QuadrantBounds];
        if (!bounds) return;

        const x = this.random.uniform(bounds.minX, bounds.maxX);
        const y = this.random.uniform(bounds.minY, bounds.maxY);
        const rotation = this.getRotation();

        piece.setPosition({ x, y });
        piece.setRotation(rotation);

        this.saveLayoutData(piece, { x, y }, rotation);
    }

    /**
     * 保存布局数据
     */
    private saveLayoutData(piece: PuzzlePiece, position: Vector2, rotation: number): void {
        this.layoutData.set(piece.id, {
            pieceId: piece.id,
            isEdge: piece.isEdge,
            complexityLevel: piece.complexityLevel,
            homeQuadrant: piece.homeQuadrant,
            initialPosition: position,
            initialRotation: rotation
        });
    }

    /**
     * 获取碎片的布局数据
     */
    getLayoutData(pieceId: string): PieceLayoutData | undefined {
        return this.layoutData.get(pieceId);
    }

    /**
     * 获取所有布局数据
     */
    getAllLayoutData(): Map<string, PieceLayoutData> {
        return this.layoutData;
    }

    /**
     * 获取与指定碎片同环/同区域的碎片 ID 列表
     * 用于视觉退让功能
     */
    getSameRegionPieces(pieceId: string): string[] {
        const layout = this.layoutData.get(pieceId);
        if (!layout) return [];

        const result: string[] = [];

        for (const [id, data] of this.layoutData) {
            if (id === pieceId) continue;

            // 边缘碎片同属一个区域
            if (layout.isEdge && data.isEdge) {
                result.push(id);
            }
            // 非边缘碎片按复杂度或象限判断
            else if (!layout.isEdge && !data.isEdge) {
                // 同复杂度或同象限视为同区域
                if (layout.complexityLevel === data.complexityLevel ||
                    layout.homeQuadrant === data.homeQuadrant) {
                    result.push(id);
                }
            }
        }

        return result;
    }

    /**
     * 工具方法：打乱数组
     */
    private shuffleArray<T>(array: T[]): void {
        for (let i = array.length - 1; i > 0; i--) {
            const j = this.random.int(0, i);
            [array[i], array[j]] = [array[j], array[i]];
        }
    }

    /**
     * 更新配置
     */
    updateConfig(config: Partial<LayoutConfig>): void {
        this.config = { ...this.config, ...config };
        // 重新计算区域边界以适应新配置
        this.calculateLayoutBoundsSideBalanced();
    }

    /**
     * 获取配置
     */
    getConfig(): LayoutConfig {
        return { ...this.config };
    }

    /**
     * 设置是否启用随机旋转（调试用）
     */
    setEnableRandomRotation(enable: boolean): void {
        this.enableRandomRotation = enable;
    }

    /**
     * 获取碎片的旋转角度
     * 如果禁用随机旋转，返回 0；否则返回随机的 60 度倍数
     */
    private getRotation(): number {
        if (!this.enableRandomRotation) {
            return 0;
        }
        return this.random.int(0, 5) * 60;
    }

    /**
     * 销毁
     */
    destroy(): void {
        this.complexityAnalyzer.destroy();
        this.layoutData.clear();
    }
}
